import { Button, Dropdown, Link, QuickLink, SelectItem, Spinner as FormdsSpinner } from '@lyse-as/formds-react';
import { ArrowForwardIcon, ChevronRightIcon, OpenInNewIcon } from '@lyse-as/formds-react/lib/icon/common';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Breakpoint, Typography as T } from 'app/component-library-wave';
import { ContainerFixed } from 'app/components/container';
import { CustomerPage } from 'app/pages/page-wrapper';
import { useAppDispatch, useAppSelector } from 'app/hooks/redux-thunk';
import { getInvoicePaymentInformation, getInvoices, getMoreInvoices } from 'app/store/actions/invoice-thunks';
import { FetchStatus } from 'app/store/root-types';
import { Spinner } from 'app/components/spinner';

import { ContainerLayoutMode } from 'app/components/container/container';
import { navigationService } from 'app/service/navigation/navigation-service';
import { iso8601ToFullMonthAndYear, iso8601ToShortDate } from 'app/utils/date-utils';
import { useAppNavigation } from 'app/utils/navigation-utils';
import { InvoiceOverview } from './invoice-overview/invoice-overview';
import { settlementIconStates, SettlementState, SETTLEMENT_ICONS } from './settlement-state/settlement-state';
import { setLoadedInvoicesFrom } from 'app/store/actions/invoice-actions';
import { useDownloadInvoicePdf } from './invoice-utils';
import { InvoiceCustomerServiceLink } from './invoice-customerservice-link/invoice-customerservice-link';
import {
  getAnonymizedCurrentPageUrl,
  getGtmFullUrl,
  GtmElementCategory,
  GtmElementCta,
} from 'app/utils/gtm-tracking-utils';
import { getDataLayers } from 'app/utils/trackingService';

import styles from './invoice.module.scss';

export const InvoicePage: CustomerPage = ({ userContext }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { fetchStatus, invoices, paymentInformation, loadedInvoicesFrom } = useAppSelector((state) => state.invoice);
  const { downloadPdf, downloadFetchStatus } = useDownloadInvoicePdf();
  const [latestInvoice, setLatestInvoice] = useState<MinesiderBackend.Invoice>();
  const [selectedBillingAccountId, setSelectedBillingAccountId] = useState<string>();
  const { goToPath } = useAppNavigation();

  useEffect(() => {
    if (fetchStatus === FetchStatus.NOT_STARTED) {
      const today = new Date();
      const fromDate = today.setMonth(today.getMonth() - 8); // Unix time in ms
      const fromDateFormatted = new Date(fromDate).toISOString().split('T')[0]; // 'yyyy-mm-dd'
      const to = '2100-01-01';

      dispatch(setLoadedInvoicesFrom(fromDate));
      dispatch(getInvoices({ from: fromDateFormatted, to }));
      dispatch(getInvoicePaymentInformation());
    }
  }, [fetchStatus]);

  useEffect(() => {
    if (invoices) {
      setSelectedBillingAccountId(userContext.customerCrmId);
    }
  }, [invoices]);

  useEffect(() => {
    const getLatestInvoice = invoices
      .filter((invoice) => invoice.billingAccountId === selectedBillingAccountId)
      .toSorted(sortInvoicesByDateDesc)[0];
    setLatestInvoice(getLatestInvoice);
  }, [invoices, selectedBillingAccountId]);

  function loadMoreInvoices() {
    const prevFromDate = new Date(loadedInvoicesFrom || Date.now());
    const newTo = new Date(loadedInvoicesFrom || Date.now()).toISOString().split('T')[0]; // 'yyyy-mm-dd'

    const newFrom = prevFromDate.setMonth(prevFromDate.getMonth() - 12);
    const newFromFormatted = new Date(newFrom).toISOString().split('T')[0]; // 'yyyy-mm-dd'
    dispatch(setLoadedInvoicesFrom(newFrom));

    dispatch(getMoreInvoices({ from: newFromFormatted, to: newTo }));

    getDataLayers().customEvents.pushButtonClickEvent({
      element_cta: GtmElementCta.INVOICE_LOAD_OLDER_INVOICES,
      element_category: GtmElementCategory.INVOICE_OVERVIEW,
      element_description: iso8601ToFullMonthAndYear(latestInvoice?.billDate || ''),
      element_status: latestInvoice?.settlementState,
      page_url: getAnonymizedCurrentPageUrl(),
    });
  }

  const sortInvoicesByDateDesc = (invoiceA: MinesiderBackend.Invoice, invoiceB: MinesiderBackend.Invoice) => {
    if (invoiceA.billDate === undefined || invoiceB.billDate === undefined) {
      return 0;
    }
    return invoiceA.billDate < invoiceB.billDate ? 1 : -1;
  };

  const getLoadedInvoicesFromMessage = () => {
    const loadedFromDate = new Date(loadedInvoicesFrom || Date.now()).toISOString();
    const formattedLoadedFromDate = iso8601ToShortDate(loadedFromDate);
    return t('pages.invoice.loadedInvoicesFromDateMessage', {
      date: formattedLoadedFromDate,
    });
  };

  const SubAccountSelection = () => {
    const uniqueBillingAccountIds = Array.from(new Set(invoices.map((invoice) => invoice.billingAccountId))).filter(
      (billingAccountId): billingAccountId is string => Boolean(billingAccountId),
    );

    if (uniqueBillingAccountIds.length <= 1) {
      return null;
    }
    return (
      <div className={styles.subAccountDropdown}>
        <Dropdown
          name="selectSubAccount"
          placeholder={t('pages.invoice.selectSubAccount')}
          value={selectedBillingAccountId ?? ''}
          onValueChange={(value) => setSelectedBillingAccountId(value)}
          className={styles.subAccountDropdown}
          label={t('pages.invoice.selectSubAccountLabel')}
        >
          {uniqueBillingAccountIds.map((billingAccountId) => (
            <SelectItem key={billingAccountId} value={billingAccountId}>
              {billingAccountId === userContext.customerCrmId
                ? `${billingAccountId} (${t('pages.invoice.mainAccountLabel')})`
                : billingAccountId}
            </SelectItem>
          ))}
        </Dropdown>
      </div>
    );
  };

  const seeInvoiceDetailsClicked = (latestInvoice: MinesiderBackend.Invoice) => {
    const fullUrl = getGtmFullUrl(`${navigationService.PAGES.invoiceDetails.url}?invoiceId=:invoiceId`);

    getDataLayers().customEvents.pushButtonClickEvent({
      element_description: iso8601ToFullMonthAndYear(latestInvoice.billDate || ''),
      element_category: GtmElementCategory.INVOICE_OVERVIEW,
      element_cta: GtmElementCta.INVOICE_VIEW_INVOICE_DETAILS,
      element_url: fullUrl,
      element_status: latestInvoice.settlementState,
      page_url: getAnonymizedCurrentPageUrl(),
    });
  };

  const createAvtaleGiroClicked = () => {
    getDataLayers().customEvents.pushButtonClickEvent({
      element_category: GtmElementCategory.INVOICE_OVERVIEW,
      element_cta: GtmElementCta.INVOICE_CREATE_AVTALEGIRO_LINK,
      element_description: iso8601ToFullMonthAndYear(latestInvoice?.billDate || ''),
      element_url: getGtmFullUrl(navigationService.PAGES.invoiceAvtaleGiro.url),
      element_status: latestInvoice?.settlementState,
      page_url: getAnonymizedCurrentPageUrl(),
    });
  };

  const invoiceListItemClicked = (invoice: MinesiderBackend.Invoice) => {
    getDataLayers().customEvents.pushButtonClickEvent({
      element_category: GtmElementCategory.INVOICE_OVERVIEW,
      element_cta: iso8601ToFullMonthAndYear(invoice?.billDate || ''),
      element_description: iso8601ToFullMonthAndYear(latestInvoice?.billDate || ''),
      element_url: getGtmFullUrl(`${navigationService.PAGES.invoiceDetails.url}?invoiceId=:invoiceId`),
      element_status: latestInvoice?.settlementState,
      page_url: getAnonymizedCurrentPageUrl(),
    });
  };

  const postponeInvoiceClicked = (latestInvoice: MinesiderBackend.Invoice) => {
    const postponeUrl = navigationService.getPostponeUrl(latestInvoice.id!);

    getDataLayers().customEvents.pushButtonClickEvent({
      element_category: GtmElementCategory.INVOICE_OVERVIEW,
      element_cta: GtmElementCta.INVOICE_POSTPONE_INVOICE_LINK,
      element_description: iso8601ToFullMonthAndYear(latestInvoice?.billDate || ''),
      element_url: getGtmFullUrl(navigationService.PAGES.invoicePostpone.url),
      element_status: latestInvoice?.settlementState,
      page_url: getAnonymizedCurrentPageUrl(),
    });

    goToPath(postponeUrl);
  };

  return (
    <ContainerFixed mode={ContainerLayoutMode.Medium} className={styles.pageContainer}>
      <T
        variant="formds-altibox-header-tertiary"
        component="h1"
        maxBreakpoint={Breakpoint.TABLET}
        bold={true}
        className={styles.pageHeading}
      >
        {t('pages.invoice.name')}
      </T>

      <SubAccountSelection />

      <T variant="formds-body" component="p" className={styles.ingress}>
        {t('pages.invoice.ingress')}
      </T>

      <T variant="formds-lead" component="h2" bold={true} className={styles.invoiceLeadHeading}>
        {t('pages.invoice.latestInvoice')}
      </T>

      <section className={styles.borderContainer}>
        {fetchStatus === FetchStatus.PENDING && !latestInvoice && <Spinner />}
        {latestInvoice && (
          <>
            <InvoiceOverview
              invoice={latestInvoice}
              paymentInformation={paymentInformation}
              elementCategory={GtmElementCategory.INVOICE_OVERVIEW}
            />
            <div className={styles.detailsButtonContainer}>
              <Button
                variant="filled"
                scheme="primary"
                style={{ width: '100%' }}
                onClick={() => downloadPdf(latestInvoice, GtmElementCategory.INVOICE_OVERVIEW)}
                disabled={downloadFetchStatus === FetchStatus.PENDING}
              >
                {downloadFetchStatus === FetchStatus.PENDING ? (
                  <span style={{ display: 'inline-flex', width: '1.25em', height: '1.25em' }}>
                    <FormdsSpinner loadingText="Laster" loadingCompleteText="Ferdig" isLoadingComplete={false} />
                  </span>
                ) : (
                  <>
                    {t('pages.invoice.downloadInvoice')}
                    <OpenInNewIcon />
                  </>
                )}
              </Button>
              <Button
                style={{ width: '100%' }}
                variant="outlined"
                scheme="primary"
                onClick={() => postponeInvoiceClicked(latestInvoice)}
              >
                {t('pages.invoice.postponeInvoice')}
              </Button>
              <Link
                trailingIcon={<ArrowForwardIcon />}
                className={styles.detailsLink}
                href={`${navigationService.PAGES.invoiceDetails.url}?invoiceId=${latestInvoice.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  seeInvoiceDetailsClicked(latestInvoice);
                  goToPath(`${navigationService.PAGES.invoiceDetails.url}?invoiceId=${latestInvoice.id}`);
                }}
              >
                {t('pages.invoice.seeInvoiceDetails')}
              </Link>
            </div>
          </>
        )}
        {!latestInvoice && fetchStatus !== FetchStatus.PENDING && (
          <T variant="formds-body" component="p">
            {t('pages.invoice.noRecentInvoices')}
          </T>
        )}
      </section>

      <section>
        <T variant="formds-lead" component="h2" bold={true}>
          {t('pages.invoice.previousInvoices')}
        </T>
        <T variant="formds-body" component="p" className={styles.loadedInvoicesFromMessage} aria-live="polite">
          {getLoadedInvoicesFromMessage()}
        </T>
        <div className={styles.borderContainer}>
          {invoices.filter((invoice) => invoice.billingAccountId === selectedBillingAccountId).length <= 1 &&
          fetchStatus === FetchStatus.FULFILLED ? (
            <T variant="formds-body" component="p">
              {t('pages.invoice.noInvoicesInSelectedDateRange')}
            </T>
          ) : (
            <div
              className={styles.previousInvoicesList}
              style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
            >
              {invoices
                .filter(
                  (invoice) =>
                    invoice.id !== latestInvoice?.id && invoice.billingAccountId === selectedBillingAccountId,
                )
                .toSorted(sortInvoicesByDateDesc)
                .map((invoice) => {
                  const { iconName, i18nKey } = settlementIconStates({
                    settlementState: invoice.settlementState as SettlementState,
                    dueDate: invoice.dueDate,
                  });
                  const SettlementGlyph = SETTLEMENT_ICONS[iconName];

                  return (
                    <QuickLink
                      key={invoice.id}
                      title={iso8601ToFullMonthAndYear(invoice.billDate || '')}
                      description={t(i18nKey)}
                      href={`${navigationService.PAGES.invoiceDetails.url}?invoiceId=${invoice.id}`}
                      icon={SettlementGlyph ? <SettlementGlyph /> : undefined}
                      rightIcon={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                          {`${invoice.taxIncludedSum} kr`}
                          <ChevronRightIcon />
                        </span>
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        invoiceListItemClicked(invoice);
                        goToPath(`${navigationService.PAGES.invoiceDetails.url}?invoiceId=${invoice.id}`);
                      }}
                    />
                  );
                })}
            </div>
          )}
        </div>

        <div className={styles.centerContainer}>
          <Button
            className={styles.loadMoreInvoices}
            onClick={loadMoreInvoices}
            disabled={fetchStatus === FetchStatus.PENDING}
            variant="filled"
            scheme="primary"
          >
            {fetchStatus === FetchStatus.PENDING ? (
              <span style={{ display: 'inline-flex', width: '1.25em', height: '1.25em' }}>
                <FormdsSpinner
                  loadingText={t('pages.invoice.loadingInvoices')}
                  loadingCompleteText="Ferdig"
                  isLoadingComplete={false}
                />
              </span>
            ) : (
              <>{t('pages.invoice.loadOlderInvoices')}</>
            )}
          </Button>
        </div>
      </section>

      {paymentInformation?.paymentMethod !== 'AVTALEGIRO' && (
        <section className={styles.avtaleGiro}>
          <T variant="formds-lead" component="h2" bold={true} className={styles.invoicingQuestionsHeader}>
            {t('pages.invoice.avtaleGiro')}
          </T>
          <T component="p" className={styles.invoicingQuestionsHeader} variant="formds-body">
            {t('pages.invoice.avtaleGiroDescription')}
          </T>
          <Link
            href={navigationService.PAGES.invoiceAvtaleGiro.url}
            trailingIcon={<OpenInNewIcon />}
            onClick={(e) => {
              e.preventDefault();
              createAvtaleGiroClicked();
              goToPath(navigationService.PAGES.invoiceAvtaleGiro.url);
            }}
          >
            {t('pages.invoice.createAvtaleGiro')}
          </Link>
        </section>
      )}

      <div className={styles.pageBottomMargin}>
        <InvoiceCustomerServiceLink />
      </div>
    </ContainerFixed>
  );
};
